import React, { useState, useEffect } from "react";
import { ImSearch } from "react-icons/im";
import Breadcrumb from "../../../components/layout/Breadcrumb";
import { useDispatch, useSelector } from "react-redux";
import { fetchCandidatePartialData } from "../../../redux/slice/getPartialCandidateDataSlice";
import { fetchSearchData } from "../../../redux/slice/searchDataSlice";
import { OverlayTrigger, Tooltip } from "react-bootstrap";
import { toast, ToastContainer } from "react-toastify";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import { useHasPermission } from "../../../hooks/useHasPermission";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import PopupState, { bindTrigger, bindMenu } from "material-ui-popup-state";
import Pagination from '@mui/material/Pagination';
import Stack from '@mui/material/Stack';
import createCredential from "../../../assets/img/create-credential.png";
import CreateCredentialModal from "../../../features/portal/candidate/CreateCredentialModal";

export default function ApprovedCandidateList() {
  const dispatch = useDispatch();
  const { Candidates, status } = useSelector(
    (state) => state.Candidates
  );
  const searchDataList = useSelector(
    (state) => state.SearchData.SearchDataList
  );

  const [selectedCandidateName, setSelectedCandidateName] = useState(null);
  const [credentialModal, setCredentialModal] = useState(false);
  const [totalItems, setTotalItems] = useState(Candidates?.totalItems ?? 0);
  const [totalPages, setTotalPages] = useState(Candidates?.totalPages ?? 1);
  const [currentPage, setCurrentPage] = useState(Candidates?.currentPage ?? 1);
  const [statusCount, setStatusCount] = useState(Candidates?.statusCount ?? []);
  const [restCandidate, setRestCandidate] = useState([]);
  const [pageSize, setPageSize] = useState(20);
  const [displayedCandidates, setDisplayedCandidates] = useState(restCandidate);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus] = useState('');
  const [sortBy, setSortBy] = useState("firstName");
  const [direction, setDirection] = useState("desc")

  const canViewCandidate = useHasPermission("Candidate-view");
  const canViewExport = useHasPermission("Export Offer Letter-view");
  const canEditExport = useHasPermission("Export Offer Letter-manage");
  const canViewConvertToEmp = useHasPermission("Convert Employee-view");
  const canEditConvertToEmp = useHasPermission("Convert Employee-manage");
  const canViewRole = useHasPermission("Role-view");

  useEffect(() => {
    setTotalItems(searchDataList?.totalItems)
    setTotalPages(searchDataList?.totalPages)
    setCurrentPage(searchDataList?.pageNo)
    setStatusCount(searchDataList?.statusCount)
    setRestCandidate(searchDataList?.result);
    setDisplayedCandidates(searchDataList?.result);

  }, [searchDataList]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setTotalItems(Candidates?.totalItems)
      setTotalPages(Candidates?.totalPages)
      setCurrentPage(Candidates?.currentPage)
      setStatusCount(Candidates?.statusCount)
      setRestCandidate(Candidates?.content);
      setDisplayedCandidates(Candidates?.content);
    }
  }, [dispatch, Candidates, totalPages, totalItems, currentPage, searchTerm, statusCount, filterStatus, sortBy, direction]);

  useEffect(() => {
    if (canViewCandidate) {
      dispatch(fetchCandidatePartialData({ page: 1, SB: sortBy, order: direction, candidateStatus: "APPROVED", size: pageSize }))
    }
  }, [dispatch, canViewCandidate]);

  useEffect(() => {
    if (!searchTerm || searchTerm.length < 1) {
      return;
    }

    const timerId = setTimeout(() => {
      dispatch(fetchSearchData({ type: "candidate", keyword: searchTerm, page: 0, size: pageSize}));
    }, 1000);
    return () => {
      clearTimeout(timerId);
    };
  }, [searchTerm, dispatch]);

  const approvedCandidate = restCandidate?.filter(
    user => user?.documentUploadStatus === "APPROVED"
  );

  const renderTooltip = (props, description) => (
    <Tooltip id="button-tooltip" {...props}>
      {description}
    </Tooltip>
  );

  const handleExport = () => {
    if (!canEditExport) {
      toast.error("You don't have permission to export data", {
        className: "toast-error-custom",
        autoClose: 5000,
      });
      return;
    }
    const exportData = displayedCandidates?.map((candidate) => ({
      Name: candidate.fullName ?? candidate.firstName,
      "Email ID": candidate.email ?? "-",
      "Date Of Birth":
        new Date(candidate.creationDate).toISOString().split("T")[0] ?? "-",
      "Mob. No.": candidate.mobile ?? "-",
    }));
    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Candidates");
    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
    });
    saveAs(data, "candidates_report.xlsx");
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return { date: "N/A", time: "N/A" };

    const dateObj = new Date(isoString);
    const date = dateObj.toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });

    const time = dateObj.toLocaleTimeString("en-IN", {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });

    return { date, time };
  };
  const handleSortBy = (event) => {
    const selectedFilter = event.target.value;

    switch (selectedFilter) {
      case "Name":
        setSortBy("firstName");
        dispatch(fetchCandidatePartialData({ page: 1, SB: "firstName", order: direction, candidateStatus: "APPROVED", size: pageSize }));
        break;

      case "Email Id":
        setSortBy("email");
        dispatch(fetchCandidatePartialData({ page: 1, SB: "email", order: direction, candidateStatus: "APPROVED",  size: pageSize }));
        break;

      case "Mobile No.":
        setSortBy("mobile");
        dispatch(fetchCandidatePartialData({ page: 1, SB: "mobile", order: direction, candidateStatus: "APPROVED", size: pageSize }));
        break;

      case "Invited By":
        setSortBy("invitedBy");
        dispatch(fetchCandidatePartialData({ page: 1, SB: "invitedBy", order: direction, candidateStatus: "APPROVED", size: pageSize }));
        break;

      case "Invitation Date":
        setSortBy("creationDate");
        dispatch(fetchCandidatePartialData({ page: 1, SB: "creationDate", order: direction, candidateStatus: "APPROVED", size: pageSize }));
        break;

      default:
        break;
    }

    setDisplayedCandidates(Candidates?.content);
  };

  const handleOrderBy = (event) => {
    const selectedFilter = event.target.value;

    switch (selectedFilter) {
      case "Ascending":
        setDirection("asc");
        dispatch(fetchCandidatePartialData({ page: 1, SB: sortBy, order: "asc", candidateStatus: "APPROVED", size: pageSize }));
        break;

      case "Descending":
        setDirection("desc");
        dispatch(fetchCandidatePartialData({ page: 1, SB: sortBy, order: "desc", candidateStatus: "APPROVED", size: pageSize }));
        break;

      default:
        break;
    }

    setDisplayedCandidates(Candidates?.content);
  };

  const toSentenceCase = (str) => {
    if (!str) return "";
    const normalizedStr = str.toLowerCase().replaceAll(/_/g, " ");
    return normalizedStr.charAt(0).toUpperCase() + normalizedStr.slice(1);
  };


  const handlePageChange = (_, value) => {
    if (canViewCandidate) {
      if (searchTerm === '') {
        dispatch(fetchCandidatePartialData({ page: value, SB: sortBy, order: direction, candidateStatus: "APPROVED" }));
      } else {
        dispatch(fetchSearchData({ type: "candidate", keyword: searchTerm, page: value }));
      }

    }
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
  };


  const handleFormSubmit = (e) => {
    e.preventDefault();
    if (searchTerm) {
      dispatch(fetchSearchData({ type: "candidate", keyword: searchTerm, page: 0 }));
    }
  };

  if (!canViewConvertToEmp) {
    return (
      <p className="perm-msg">
        You don’t have permission to see the list of approved candidates!
      </p>
    );
  }

  return (
    <div className="container-fluid">
      <div
        className=" d-flex justify-content-between align-items-center"
        style={{ flexDirection: "row-reverse", marginBottom: "18px" }}
      >
        <div className="icons-list">
          <Breadcrumb />
        </div>
      </div>

      {/* user list table */}
      <div className="section-body">
        <div className="container-fluid">
          <div className="tab-content">
            <div
              className="tab-pane fade active show"
              id="Employee-list"
              role="tabpanel"
            >
              <div className="card">
                <div className="card-header">

                  <div className="employeelist-header">
                    <div className="d-flex align-items-center  "
                      style={{ display: "flex", alignItems: "center", gap: "10px" }}>
                      <div>
                        <h3 className="card-title mb-0 ml-4 ">Sort By :</h3>
                      </div>
                      <div>
                        <form>
                          <div className="d-flex align-items-center">
                            <select
                              className="custom-select"
                              onChange={handleOrderBy}
                            >
                              <option>Ascending</option>
                              <option>Descending </option>
                            </select>
                          </div>
                        </form>
                      </div>
                      <div>
                        <form>
                          <div className="d-flex align-items-center">
                            <select
                              className="custom-select"
                              onChange={handleSortBy}
                            >
                              <option>Name</option>
                              <option>Email Id</option>
                              <option>Mobile No.</option>
                              <option>Invited By</option>
                              <option>Invitation Date</option>
                            </select>
                          </div>
                        </form>
                      </div>
                    </div>
                    <div>
                      <OverlayTrigger
                        placement="top"
                        delay={{ show: 250, hide: 400 }}
                        overlay={(props) =>
                          renderTooltip(
                            props,
                            "Search by candidate name, id or mobile no."
                          )
                        }
                      >
                        <form className="user-searchForm ml-4" onSubmit={handleFormSubmit}>
                          <input
                            className="form-control me-2 search"
                            type="search"
                            placeholder="Search candidate"
                            aria-label="Search"
                            value={searchTerm}
                            onChange={handleInputChange}
                          />
                          <button type="submit" className="btn-interactive" onClick={handleFormSubmit} aria-label="Submit Search">
                            <ImSearch className="searchIcon" />
                          </button>
                        </form>
                      </OverlayTrigger>
                    </div>

                  </div>
                  {/* <button
                    className="primary-btn pl-4 pr-4"
                    title={
                      canViewExport
                        ? "export data"
                        : "No permission to export data"
                    }
                    disabled={!canViewExport}
                    onClick={canEditExport ? handleExport : undefined}
                  >
                    Export
                  </button> */}
                  <div className="card-body">
                    {status === "loading" && (
                      <p className="loading">Loading...</p>
                    )}

                    {status === "failed" && (
                      <p className="server-error-msg">
                        Unable to connect to server
                      </p>
                    )}
                    {status === "succeeded" && (
                      <div className="role-data table-responsive" style={{ width: "100%" ,overflow: "auto" }}>
                        <table id="candidate-table" className="role-data-table table table-hover table-vcenter text-nowrap mb-0" >
                          <thead>
                            <tr>
                              <th className="name-column table-row" style={{ width: "55px", textAlign: "center" }}>S.No.</th>
                              <th className="name-column table-row">Name</th>
                              <th className="table-row">Candidate ID</th>
                              <th className="table-row">Email ID</th>
                              <th className="table-row">Mobile No.</th>
                              <th className="table-row">Invited By</th>
                              <th className="table-row">Invitation Date</th>
                              <th className="table-row" style={{ width: "95px", textAlign: "center" }}>Status</th>
                              <th
                                ref={(el) => {
                                  if (el) {
                                    el.style.setProperty("padding", "0", "important");
                                    el.style.setProperty("width", "55px");
                                    el.style.setProperty("text-align", "center");
                                  }
                                }}
                              >Action</th>
                            </tr>
                          </thead>
                          {approvedCandidate?.length === 0 ? (
                            <tbody>
                              <td colSpan={9}><p className="server-error-msg server-error-msg">-- No record found for approved candidates --</p></td>
                            </tbody>
                          ) : (
                            <tbody>
                              {approvedCandidate?.map((list, index) => {
                                return (
                                  <tr key={list.userId}>
                                    <td>{(index + 1) + (currentPage * 10)}.</td>
                                    <td>{list.fullName ?? list.firstName}</td>
                                    <td>{list.userId ? `cand-0${list.userId}` : "-"}</td>
                                    <td>
                                    <OverlayTrigger
                                    placement="top"
                                     overlay={
                                    <Tooltip id={`email-tooltip-${list.userId}`}>
                                    {list?.email ?? "-"}
                                    </Tooltip>
                                }
                                  >
                               <span style={{ cursor: 'pointer' }}>
                                  {list?.email ?? "-"}
                               </span>
                                </OverlayTrigger>
                                    </td>
                                    <td>{list.mobile ?? "-"}</td>
                                   <td>
                                   <OverlayTrigger
                                   placement="top"
                                    overlay={
                                  <Tooltip id={`invited-tooltip-${list.userId}`}>
                                   {list?.invitedBy ?? "-"}
                                    </Tooltip>
                                  }
                               > 
                              <span style={{ cursor: 'pointer' }}>
                               {list?.invitedBy ?? "-"}
                             </span>
                            </OverlayTrigger>
                                 </td>
                                    <td>{formatDateTime(list.creationDate)?.date ?? "-"}</td>
                                    <td>
                                      {list.documentUploadStatus
                                        ? toSentenceCase(
                                          list.documentUploadStatus
                                        )
                                        : "PENDING"}
                                    </td>
                                    <td className="action-column">
                                      <PopupState
                                        variant="popover"
                                        popupId="demo-popup-menu"
                                      >
                                        {(popupState) => (
                                          <React.Fragment>
                                            <button
                                              type="button"
                                              className="btn btn-icon btn-sm icons"
                                              title="view"
                                              {...bindTrigger(popupState)}
                                            >
                                              <img
                                                src={require("assets/img/more.png")}
                                                alt="view"
                                                className="action-icon"
                                                style={{
                                                  opacity:
                                                    canViewConvertToEmp
                                                      ? 1
                                                      : 0.5,
                                                }}
                                              />
                                            </button>
                                            {/* </Button> */}
                                            <Menu {...bindMenu(popupState)}>
                                              <MenuItem
                                                onClick={() => {
                                                  if (canViewConvertToEmp) {
                                                    popupState.close();
                                                    setSelectedCandidateName({ name: list.fullName, id: list.userId });
                                                    setCredentialModal(true);
                                                  }
                                                }}
                                                disabled={!canViewConvertToEmp}
                                              >
                                                <button
                                                  type="button"
                                                  className="btn btn-icon btn-sm icons"
                                                  title={
                                                    canViewConvertToEmp
                                                      ? "Generate credentials for login"
                                                      : "No permission to generate credentials for login"
                                                  }
                                                  disabled={!canViewConvertToEmp}
                                                >
                                                  <img
                                                    src={createCredential}
                                                    alt="view"
                                                    className="action-icon"
                                                    style={{
                                                      opacity:
                                                        canViewConvertToEmp
                                                          ? 1
                                                          : 0.5,
                                                    }}
                                                  />
                                                </button>{" "}
                                                Convert to employee
                                              </MenuItem>
                                            </Menu>
                                          </React.Fragment>
                                        )}
                                      </PopupState>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>)}
                        </table>
                        {approvedCandidate?.length > 0 ? (
                          <Stack spacing={2} sx={{ marginTop: 3, alignItems: 'center' }}>
                            <Pagination
                              count={totalPages}
                              page={currentPage + 1}
                              onChange={handlePageChange}
                              shape="rounded"
                              color="primary"
                              disabled={status === 'loading'}
                            />
                          </Stack>
                        ) : null}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer />
      {credentialModal && (
        <CreateCredentialModal
          show={credentialModal}
          onHide={() => setCredentialModal(false)}
          canEdit={canEditConvertToEmp}
          candidate={selectedCandidateName}
          hasRolePermission={canViewRole}
        />
      )
      }
    </div>
  )
}
